import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ManagementPanelComponent } from '@connect-services/client/components/management/management-panel/management-panel.component';
import { TopbarComponent } from '@connect-services/client/components/topbar/topbar.component';
import { VersionComponent } from '@connect-services/client/components/version/version.component';
import { detailPanelOutletRegex } from '@connect-services/client/guards/can-navigate.guard';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FaqService } from '@connect-services/client/services/faq.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoadingComponent,
        ManagementPanelComponent,
        RessourcesModule,
        RouterOutlet,
        TopbarComponent,
        VersionComponent,
    ],
    selector: 'app-management',
    styleUrl: './management.component.scss',
    templateUrl: './management.component.html',
})
export class ManagementComponent {
    protected readonly faqService = inject(FaqService);
    protected readonly isDetailPanelOpened: WritableSignal<boolean> = signal(false);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly securityService = inject(SecurityService);

    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    public constructor() {
        this.togglePanel();

        this.router.events.pipe(filter((event: Event) => event instanceof NavigationEnd)).subscribe(() => {
            this.togglePanel();
        });
    }

    protected async backToHome(): Promise<void> {
        const path = RoutePathEnum.PROJECTS;
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            await this.router.navigate([path]);

            return;
        }

        await this.router.navigate([path, projectId]);
    }

    private togglePanel(): void {
        if (this.router.url.match(detailPanelOutletRegex)) {
            this.isDetailPanelOpened.set(true);

            return;
        }

        this.isDetailPanelOpened.set(false);
    }
}
